import { useRef, useState, useCallback, useEffect } from "react";

/**
 * BeforeAfterSlider — drag-to-reveal comparison slider.
 * Props:
 *   beforeSrc, afterSrc - image URLs
 *   beforeAlt, afterAlt - accessibility text
 *   height - tailwind height classes (default 'h-72 sm:h-96')
 */
export default function BeforeAfterSlider({
  beforeSrc,
  afterSrc,
  beforeAlt = "Before",
  afterAlt = "After",
  height = "h-72 sm:h-96",
  beforeLabel = "Before",
  afterLabel = "After",
  testid,
}) {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50); // 0-100 (percent from left)
  const dragging = useRef(false);

  const updateFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = clientX - rect.left;
    const pct = Math.max(0, Math.min(100, (x / rect.width) * 100));
    setPos(pct);
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    updateFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (!dragging.current) return;
    updateFromClientX(e.clientX);
  };
  const onPointerUp = (e) => {
    dragging.current = false;
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* noop */ }
  };

  // Keyboard support on the handle
  const onKeyDown = (e) => {
    if (e.key === "ArrowLeft") setPos((p) => Math.max(0, p - 4));
    else if (e.key === "ArrowRight") setPos((p) => Math.min(100, p + 4));
    else if (e.key === "Home") setPos(0);
    else if (e.key === "End") setPos(100);
  };

  // Subtle auto-hint on first mount
  useEffect(() => {
    const t1 = setTimeout(() => setPos(38), 600);
    const t2 = setTimeout(() => setPos(62), 1100);
    const t3 = setTimeout(() => setPos(50), 1600);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  return (
    <div
      ref={containerRef}
      className={`relative ${height} w-full rounded-2xl overflow-hidden select-none ba-card touch-none bg-gray-200`}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      data-testid={testid}
    >
      {/* After (base, full) */}
      <img
        src={afterSrc}
        alt={afterAlt}
        draggable={false}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />
      {/* Before (clipped to left of handle) */}
      <img
        src={beforeSrc}
        alt={beforeAlt}
        draggable={false}
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
      />

      {/* Labels */}
      <div className="absolute top-3 left-3 text-[10px] sm:text-xs uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full bg-black/70 text-white">
        {beforeLabel}
      </div>
      <div className="absolute top-3 right-3 text-[10px] sm:text-xs uppercase tracking-widest font-semibold px-2.5 py-1 rounded-full bg-[#0EA5E9] text-white">
        {afterLabel}
      </div>

      {/* Divider line */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.15)]"
        style={{ left: `calc(${pos}% - 1px)` }}
      />

      {/* Handle */}
      <button
        type="button"
        aria-label="Drag to compare before and after"
        onKeyDown={onKeyDown}
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 rounded-full bg-white shadow-lg ring-1 ring-black/10 flex items-center justify-center cursor-ew-resize hover:scale-105 transition-transform"
        style={{ left: `${pos}%` }}
        data-testid={testid ? `${testid}-handle` : undefined}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-[#0369A1]">
          <polyline points="15 18 9 12 15 6" />
          <polyline points="9 6 15 12 9 18" />
        </svg>
      </button>
    </div>
  );
}
